import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Upload, FileText, Video, Edit, Trash, File } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { supabase } from "@/integrations/supabase/client";
import { AssessmentFormBuilder } from "./AssessmentFormBuilder";

interface TrainingModule {
  id: string;
  title_en: string;
  title_el: string;
  description_en: string;
  description_el: string;
  duration_minutes: number;
}

interface TrainingSection {
  id: string;
  module_id: string;
  title_en: string;
  title_el: string;
  section_type: string;
  order_index: number;
  presentation_file_path?: string | null;
  video_file_path?: string | null;
  created_at?: string | null;
  created_by?: string | null;
  is_active?: boolean | null;
  updated_at?: string | null;
}

interface TrainingMaterial {
  id: string;
  training_topic_id: string;
  title_en: string;
  title_el: string;
  material_type: string;
  content_url?: string | null;
  content_data?: any;
  language: string;
  sort_order?: number | null;
  is_active?: boolean | null;
  duration_seconds?: number | null;
  file_size_bytes?: number | null;
  mime_type?: string | null;
  created_at: string;
  updated_at: string;
}

interface AssessmentForm {
  id: string;
  module_id: string;
  title_en: string;
  title_el: string;
  pass_percentage: number;
}

interface AdminTrainingEditorProps {
  moduleId: string;
  onUpdate: () => void;
}

// Diagnostic function to check setup
const checkStorageSetup = async () => {
  const diagnostics = {
    authenticated: false,
    hasProfile: false,
    organizationId: null as string | null,
    buckets: {
      'training-videos': false,
      'training-presentations': false,
      'training-materials': false
    },
    canInsertSections: false,
    errors: [] as string[]
  };

  try {
    // Check authentication
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) {
      diagnostics.errors.push('Not authenticated');
      return diagnostics;
    }
    diagnostics.authenticated = true;

    // Check profile
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('role')
      .eq('id', user.id)
      .single();
    
    if (profileError) {
      diagnostics.errors.push(`Profile error: ${profileError.message}`);
    } else {
      diagnostics.hasProfile = true;
      diagnostics.organizationId = user.id;
    }

    // Check storage buckets
    const { data: buckets, error: bucketsError } = await supabase.storage.listBuckets();
    if (bucketsError) {
      diagnostics.errors.push(`Cannot list buckets: ${bucketsError.message}`);
    } else if (buckets) {
      buckets.forEach(bucket => {
        if (bucket.name in diagnostics.buckets) {
          diagnostics.buckets[bucket.name as keyof typeof diagnostics.buckets] = true;
        }
      });
    }

    // Test database access
    const { error: testError } = await supabase
      .from('training_sections')
      .select('id')
      .limit(1);
    
    if (testError) {
      diagnostics.errors.push(`Database access error: ${testError.message}`);
    } else {
      diagnostics.canInsertSections = true;
    }

  } catch (error: any) {
    diagnostics.errors.push(`Unexpected error: ${error.message}`);
  }

  return diagnostics;
};

export const AdminTrainingEditor = ({ moduleId, onUpdate }: AdminTrainingEditorProps) => {
  const { language } = useLanguage();
  const [isEditing, setIsEditing] = useState(false);
  const [module, setModule] = useState<TrainingModule | null>(null);
  const [sections, setSections] = useState<TrainingSection[]>([]);
  const [materials, setMaterials] = useState<TrainingMaterial[]>([]);
  const [assessmentForm, setAssessmentForm] = useState<AssessmentForm | null>(null);
  const [diagnostics, setDiagnostics] = useState<any>(null);

  useEffect(() => {
    fetchModuleData();
    fetchSections();
    fetchMaterials();
    fetchAssessmentForm();
    
    // Run diagnostics
    checkStorageSetup().then(result => {
      console.log('=== STORAGE SETUP DIAGNOSTICS ===');
      console.log('Authenticated:', result.authenticated);
      console.log('Has Profile:', result.hasProfile);
      console.log('Organization ID:', result.organizationId);
      console.log('Buckets Found:');
      Object.entries(result.buckets).forEach(([bucket, exists]) => {
        console.log(`  - ${bucket}:`, exists ? '✅' : '❌ MISSING');
      });
      console.log('Can Access Database:', result.canInsertSections);
      if (result.errors.length > 0) {
        console.error('Errors found:', result.errors);
      }
      console.log('=================================');
      setDiagnostics(result);
    });
  }, [moduleId]);

  const fetchModuleData = async () => {
    try {
      const { data, error } = await supabase
        .from('training_modules')
        .select('*')
        .eq('id', moduleId)
        .single();
      
      if (error) throw error;
      setModule(data);
    } catch (error) {
      console.error('Error fetching module:', error);
      toast.error('Failed to load module data');
    }
  };

  const fetchSections = async () => {
    try {
      const { data, error } = await supabase
        .from('training_sections')
        .select('*')
        .eq('module_id', moduleId)
        .order('order_index');
      
      if (error) throw error;
      setSections(data || []);
    } catch (error) {
      console.error('Error fetching sections:', error);
    }
  };

  const fetchMaterials = async () => {
    try {
      const { data, error } = await supabase
        .from('training_materials')
        .select('*')
        .eq('training_topic_id', moduleId);
      
      if (error) throw error;
      setMaterials(data || []);
    } catch (error) {
      console.error('Error fetching materials:', error);
    }
  };

  const fetchAssessmentForm = async () => {
    try {
      // Fetch all forms for this module (in case there are duplicates)
      const { data: forms, error } = await supabase
        .from('assessment_forms')
        .select('*')
        .eq('module_id', moduleId)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      if (forms && forms.length > 0) {
        if (forms.length > 1) {
          console.warn(`Found ${forms.length} assessment forms for module ${moduleId}. Using the most recent one.`);
          // Delete older duplicates
          const olderFormIds = forms.slice(1).map(f => f.id);
          if (olderFormIds.length > 0) {
            await supabase
              .from('assessment_forms')
              .delete()
              .in('id', olderFormIds);
            console.log('Deleted duplicate assessment forms:', olderFormIds);
          }
        }
        setAssessmentForm(forms[0]);
      }
    } catch (error) {
      console.error('Error fetching assessment form:', error);
    }
  };

  const updateModule = async (updates: any) => {
    try {
      const { error } = await supabase
        .from('training_modules')
        .update(updates)
        .eq('id', moduleId);
      
      if (error) throw error;
      toast.success('Module updated successfully');
      onUpdate();
      fetchModuleData();
    } catch (error) {
      console.error('Error updating module:', error);
      toast.error('Failed to update module');
    }
  };

  const handleFileUpload = async (file: File, type: 'presentation' | 'video' | 'material') => {
    try {
      // Get current user and organization info
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');
      
      // Use user ID as organization identifier
      const orgId = user.id;
      
      // Validate file type
      const fileExt = file.name.split('.').pop()?.toLowerCase();
      const allowedTypes = {
        video: ['mp4', 'webm', 'mov', 'avi'],
        presentation: ['ppt', 'pptx', 'pdf'],
        material: ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'txt']
      };
      
      if (!fileExt || !allowedTypes[type].includes(fileExt)) {
        throw new Error(`Invalid file type. Allowed types for ${type}: ${allowedTypes[type].join(', ')}`);
      }
      
      // Check file size (max 100MB for videos, 50MB for others)
      const maxSize = type === 'video' ? 100 * 1024 * 1024 : 50 * 1024 * 1024;
      if (file.size > maxSize) {
        throw new Error(`File too large. Max size: ${maxSize / (1024 * 1024)}MB`);
      }
      
      const bucket = type === 'presentation' ? 'training-presentations' 
                   : type === 'video' ? 'training-videos' 
                   : 'training-materials';
      
      // Include organization ID in path for isolation
      const fileName = `org_${orgId}/${moduleId}/${Date.now()}_${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
      
      // First check if bucket exists and create if not (for error logging)
      const { data: buckets, error: bucketsError } = await supabase.storage.listBuckets();
      if (bucketsError) {
        console.error('Error listing buckets:', bucketsError);
        throw new Error('Storage configuration error. Please contact administrator.');
      }
      
      const bucketExists = buckets?.some(b => b.name === bucket);
      if (!bucketExists) {
        throw new Error(`Storage bucket '${bucket}' not configured. Please contact administrator to set up storage.`);
      }
      
      // Upload file with proper error handling
      const { data, error: uploadError } = await supabase.storage
        .from(bucket)
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false,
          contentType: file.type || 'application/octet-stream'
        });
      
      if (uploadError) {
        console.error('Upload error details:', uploadError);
        if (uploadError.message?.includes('row-level security')) {
          throw new Error('Permission denied. Please ensure you have upload permissions.');
        } else if (uploadError.message?.includes('payload too large')) {
          throw new Error('File too large for upload.');
        } else if (uploadError.message?.includes('Invalid bucket')) {
          throw new Error('Storage bucket not properly configured. Please contact administrator.');
        }
        throw uploadError;
      }
      
      toast.success(`${type} uploaded successfully`);
      return fileName;
    } catch (error: any) {
      console.error('Error uploading file:', error);
      toast.error(error.message || 'Failed to upload file');
      return null;
    }
  };

  const addSection = async (sectionData: any) => {
    try {
      // Get current user and organization info
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated. Please login again.');
      }
      
      // Use user ID as organization identifier
      const orgId = user.id;
      
      // Prepare the section data with organization info
      const sectionToInsert = {
        ...sectionData,
        module_id: moduleId,
        order_index: sections.length,
        organization_id: orgId,
        created_by: user.id,
        is_active: true
      };
      
      console.log('Attempting to add section:', sectionToInsert);
      
      const { data, error } = await supabase
        .from('training_sections')
        .insert(sectionToInsert)
        .select();
      
      if (error) {
        console.error('Supabase error details:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code
        });
        
        // Provide specific error messages based on error type
        if (error.code === '23505') {
          throw new Error('This section already exists.');
        } else if (error.code === '23503') {
          throw new Error('Invalid module ID. Please refresh and try again.');
        } else if (error.code === '42501') {
          throw new Error('Permission denied. Please check your admin privileges.');
        } else if (error.message?.includes('violates row-level security')) {
          throw new Error('Access denied. Please ensure you have permission to add sections.');
        } else if (error.message?.includes('null value')) {
          throw new Error('Missing required fields. Please fill in all required information.');
        }
        
        throw error;
      }
      
      console.log('Section added successfully:', data);
      toast.success('Section added successfully');
      fetchSections();
    } catch (error: any) {
      console.error('Error adding section:', error);
      toast.error(error.message || 'Failed to add section. Please check console for details.');
    }
  };

  const addMaterial = async (materialData: any) => {
    try {
      // Get current user and organization info
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }
      
      // Use user ID as organization identifier
      const orgId = user.id;
      
      const { error } = await supabase
        .from('training_materials')
        .insert({
          title: materialData.title_en || materialData.title_el,
          description: materialData.description,
          file_url: materialData.content_url,
          file_type: materialData.material_type,
          module_id: moduleId,
          training_topic_id: materialData.training_topic_id || moduleId,
          organization_id: orgId,
          created_by: user.id,
          is_active: true
        });
      
      if (error) throw error;
      toast.success('Material added successfully');
      fetchMaterials();
    } catch (error) {
      console.error('Error adding material:', error);
      toast.error('Failed to add material');
    }
  };

  const deleteSection = async (sectionId: string) => {
    try {
      const { error } = await supabase
        .from('training_sections')
        .delete()
        .eq('id', sectionId);
      
      if (error) throw error;
      toast.success('Section deleted successfully');
      fetchSections();
    } catch (error) {
      console.error('Error deleting section:', error);
      toast.error('Failed to delete section');
    }
  };

  const deleteMaterial = async (materialId: string) => {
    try {
      const { error } = await supabase
        .from('training_materials')
        .delete()
        .eq('id', materialId);
      
      if (error) throw error;
      toast.success('Material deleted successfully');
      fetchMaterials();
    } catch (error) {
      console.error('Error deleting material:', error);
      toast.error('Failed to delete material');
    }
  };

  const createAssessmentForm = async () => {
    try {
      const { error } = await supabase
        .from('assessment_forms')
        .insert({
          title: `${module?.title_en || module?.title_el} Assessment`,
          description: 'Assessment form for this training module',
          passing_score: 70,
          is_active: true
        });
      
      if (error) throw error;
      toast.success('Assessment form created successfully');
      fetchAssessmentForm();
    } catch (error) {
      console.error('Error creating assessment form:', error);
      toast.error('Failed to create assessment form');
    }
  };

  if (!module) return <div>Loading...</div>;

  return (
    <div className="space-y-6">
      {/* Module Overview Editor */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>{language === 'en' ? 'Module Overview' : 'Επισκόπηση Μονάδας'}</CardTitle>
          <Button 
            variant="outline"
            onClick={() => setIsEditing(!isEditing)}
          >
            <Edit className="h-4 w-4 mr-2" />
            {isEditing ? 'Cancel' : 'Edit'}
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {isEditing ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Title (English)</label>
                  <Input 
                    value={module.title_en}
                    onChange={(e) => setModule({...module, title_en: e.target.value})}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Title (Greek)</label>
                  <Input 
                    value={module.title_el}
                    onChange={(e) => setModule({...module, title_el: e.target.value})}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Description (English)</label>
                  <Textarea 
                    value={module.description_en}
                    onChange={(e) => setModule({...module, description_en: e.target.value})}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Description (Greek)</label>
                  <Textarea 
                    value={module.description_el}
                    onChange={(e) => setModule({...module, description_el: e.target.value})}
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Duration (minutes)</label>
                <Input 
                  type="number"
                  value={module.duration_minutes}
                  onChange={(e) => setModule({...module, duration_minutes: parseInt(e.target.value)})}
                />
              </div>
              <Button onClick={() => {
                updateModule(module);
                setIsEditing(false);
              }}>
                Save Changes
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              <h3 className="text-lg font-semibold">
                {language === 'en' ? module.title_en : module.title_el}
              </h3>
              <p className="text-muted-foreground">
                {language === 'en' ? module.description_en : module.description_el}
              </p>
              <Badge variant="outline">{module.duration_minutes} minutes</Badge>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sections Management */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>{language === 'en' ? 'Learning Sections' : 'Ενότητες Μάθησης'}</CardTitle>
          <SectionDialog onAdd={addSection} />
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {sections.map((section, index) => (
              <div key={section.id} className="border rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium">
                      {language === 'en' ? section.title_en : section.title_el}
                    </h4>
                    <Badge variant="secondary">{section.section_type}</Badge>
                  </div>
                  <div className="flex items-center space-x-2">
                    {section.presentation_file_path && (
                      <Badge variant="outline">
                        <FileText className="h-3 w-3 mr-1" />
                        Presentation
                      </Badge>
                    )}
                    {section.video_file_path && (
                      <Badge variant="outline">
                        <Video className="h-3 w-3 mr-1" />
                        Video
                      </Badge>
                    )}
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => {
                        if (confirm('Are you sure you want to delete this section?')) {
                          deleteSection(section.id);
                        }
                      }}
                    >
                      <Trash className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Materials Management */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>{language === 'en' ? 'Training Materials' : 'Εκπαιδευτικό Υλικό'}</CardTitle>
          <MaterialDialog onAdd={addMaterial} />
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {materials.map((material) => (
              <div key={material.id} className="flex items-center justify-between border rounded p-3">
                <div>
                  <span className="font-medium">
                    {language === 'en' ? material.title_en : material.title_el}
                  </span>
                  <Badge variant="outline" className="ml-2">{material.material_type}</Badge>
                </div>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => {
                    if (confirm('Are you sure you want to delete this material?')) {
                      deleteMaterial(material.id);
                    }
                  }}
                >
                  <Trash className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Assessment Form Management */}
      <Card>
        <CardHeader>
          <CardTitle>{language === 'en' ? 'Assessment Form' : 'Φόρμα Αξιολόγησης'}</CardTitle>
        </CardHeader>
        <CardContent>
          <AssessmentFormBuilder 
            moduleId={moduleId} 
            onFormCreated={() => {
              fetchAssessmentForm();
              onUpdate();
            }}
          />
        </CardContent>
      </Card>
    </div>
  );
};

// Section Dialog Component
const SectionDialog = ({ onAdd }: { onAdd: (data: any) => void }) => {
  const { language } = useLanguage();
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    title_en: '',
    title_el: '',
    section_type: 'lesson'
  });
  const [presentationFile, setPresentationFile] = useState<File | null>(null);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  const handleFileUpload = async (file: File, type: 'presentation' | 'video') => {
    try {
      // Get current user and organization info
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');
      
      // Use user ID as organization identifier
      const orgId = user.id;
      
      // Validate file type
      const fileExt = file.name.split('.').pop()?.toLowerCase();
      const allowedTypes = {
        video: ['mp4', 'webm', 'mov', 'avi'],
        presentation: ['ppt', 'pptx', 'pdf']
      };
      
      if (!fileExt || !allowedTypes[type].includes(fileExt)) {
        throw new Error(`Invalid file type. Allowed types for ${type}: ${allowedTypes[type].join(', ')}`);
      }
      
      // Check file size
      const maxSize = type === 'video' ? 100 * 1024 * 1024 : 50 * 1024 * 1024;
      if (file.size > maxSize) {
        throw new Error(`File too large. Max size: ${maxSize / (1024 * 1024)}MB`);
      }
      
      const bucket = type === 'presentation' ? 'training-presentations' : 'training-videos';
      
      // Include organization ID in path
      const fileName = `org_${orgId}/${Date.now()}_${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
      
      console.log(`Uploading ${type} to:`, fileName);
      
      const { data, error: uploadError } = await supabase.storage
        .from(bucket)
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });
      
      if (uploadError) {
        console.error('Upload error:', uploadError);
        if (uploadError.message?.includes('row-level security')) {
          throw new Error('Permission denied. Please ensure storage policies are configured.');
        } else if (uploadError.message?.includes('Invalid bucket')) {
          throw new Error(`Storage bucket '${bucket}' not found. Please create it in Supabase Dashboard.`);
        }
        throw uploadError;
      }
      
      console.log('File uploaded successfully:', fileName);
      return fileName;
    } catch (error: any) {
      console.error('Error uploading file:', error);
      throw error;
    }
  };

  const handleSubmit = async () => {
    try {
      setUploading(true);
      
      // Validate required fields
      if (!formData.title_en || !formData.title_el) {
        throw new Error('Please provide both English and Greek titles');
      }
      
      let presentationPath = null;
      let videoPath = null;
      
      // Upload files if provided
      if (presentationFile) {
        try {
          presentationPath = await handleFileUpload(presentationFile, 'presentation');
        } catch (error: any) {
          throw new Error(`Presentation upload failed: ${error.message}`);
        }
      }
      
      if (videoFile) {
        try {
          videoPath = await handleFileUpload(videoFile, 'video');
        } catch (error: any) {
          throw new Error(`Video upload failed: ${error.message}`);
        }
      }
      
      const sectionData = {
        ...formData,
        presentation_file_path: presentationPath,
        video_file_path: videoPath
      };
      
      console.log('Submitting section data:', sectionData);
      
      // Call the parent's add function
      await onAdd(sectionData);
      
      // Reset form on success
      setOpen(false);
      setFormData({ title_en: '', title_el: '', section_type: 'lesson' });
      setPresentationFile(null);
      setVideoFile(null);
      
      toast.success('Section added successfully!');
    } catch (error: any) {
      console.error('Error in handleSubmit:', error);
      toast.error(error.message || 'Failed to add section');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          Add Section
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add New Section</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Title (English)</label>
              <Input 
                value={formData.title_en}
                onChange={(e) => setFormData({...formData, title_en: e.target.value})}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Title (Greek)</label>
              <Input 
                value={formData.title_el}
                onChange={(e) => setFormData({...formData, title_el: e.target.value})}
              />
            </div>
          </div>
          
          <div>
            <label className="text-sm font-medium">Type</label>
            <Select value={formData.section_type} onValueChange={(value) => setFormData({...formData, section_type: value})}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="lesson">Lesson</SelectItem>
                <SelectItem value="practical">Practical Exercise</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">
                {language === 'en' ? 'PowerPoint Presentation' : 'Παρουσίαση PowerPoint'}
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                <input
                  type="file"
                  accept=".ppt,.pptx,.pdf"
                  onChange={(e) => setPresentationFile(e.target.files?.[0] || null)}
                  className="w-full"
                />
                {presentationFile && (
                  <div className="mt-2 flex items-center text-sm text-green-600">
                    <File className="h-4 w-4 mr-1" />
                    {presentationFile.name}
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">
                {language === 'en' ? 'Video File (Optional)' : 'Αρχείο Βίντεο (Προαιρετικό)'}
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                <input
                  type="file"
                  accept=".mp4,.avi,.mov,.wmv"
                  onChange={(e) => setVideoFile(e.target.files?.[0] || null)}
                  className="w-full"
                />
                {videoFile && (
                  <div className="mt-2 flex items-center text-sm text-green-600">
                    <Video className="h-4 w-4 mr-1" />
                    {videoFile.name}
                  </div>
                )}
              </div>
            </div>
          </div>

          <Button onClick={handleSubmit} disabled={uploading || !formData.title_en || !formData.title_el}>
            {uploading ? 'Uploading...' : 'Add Section'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Material Dialog Component
const MaterialDialog = ({ onAdd }: { onAdd: (data: any) => void }) => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    title_en: '',
    title_el: '',
    file_type: 'pdf',
    content_type: 'file' // 'file' or 'link'
  });
  const [materialFile, setMaterialFile] = useState<File | null>(null);
  const [linkUrl, setLinkUrl] = useState('');
  const [uploading, setUploading] = useState(false);

  const handleFileUpload = async (file: File) => {
    try {
      const fileExt = file.name.split('.').pop()?.toLowerCase();
      const fileName = `${Date.now()}.${fileExt}`;
      
      // Determine which bucket to use based on file type
      const videoExtensions = ['mp4', 'avi', 'mov', 'wmv', 'webm'];
      const bucket = videoExtensions.includes(fileExt || '') ? 'training-videos' : 'training-materials';
      
      const { error: uploadError } = await supabase.storage
        .from(bucket)
        .upload(fileName, file);
      
      if (uploadError) throw uploadError;
      
      return fileName;
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  };

  const handleSubmit = async () => {
    try {
      setUploading(true);
      
      let contentUrl = null;
      
      if (formData.content_type === 'link') {
        // For links, use the URL directly
        contentUrl = linkUrl;
      } else if (materialFile) {
        // For files, upload to storage
        contentUrl = await handleFileUpload(materialFile);
      }
      
      const materialData = {
        title_en: formData.title_en,
        title_el: formData.title_el,
        material_type: formData.file_type,
        content_url: contentUrl,
        language: 'en'
      };
      
      onAdd(materialData);
      setOpen(false);
      setFormData({ title_en: '', title_el: '', file_type: 'pdf', content_type: 'file' });
      setMaterialFile(null);
      setLinkUrl('');
      
      toast.success('Material added successfully!');
    } catch (error) {
      console.error('Error adding material:', error);
      toast.error('Failed to add material');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          Add Material
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Training Material</DialogTitle>
          <p className="text-sm text-muted-foreground mt-2">
            Upload files, add links, or embed videos as training materials.
          </p>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium">Title (English)</label>
            <Input 
              value={formData.title_en}
              onChange={(e) => setFormData({...formData, title_en: e.target.value})}
            />
          </div>
          <div>
            <label className="text-sm font-medium">Title (Greek)</label>
            <Input 
              value={formData.title_el}
              onChange={(e) => setFormData({...formData, title_el: e.target.value})}
            />
          </div>
          <div>
            <label className="text-sm font-medium">Content Type</label>
            <Select value={formData.content_type} onValueChange={(value) => setFormData({...formData, content_type: value})}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="file">Upload File</SelectItem>
                <SelectItem value="link">Add Link/URL</SelectItem>
                <SelectItem value="video">Video Link (YouTube, Vimeo)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <label className="text-sm font-medium">Material Type</label>
            <Select value={formData.file_type} onValueChange={(value) => setFormData({...formData, file_type: value})}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pdf">PDF</SelectItem>
                <SelectItem value="document">Document (Word, Excel)</SelectItem>
                <SelectItem value="presentation">Presentation (PowerPoint)</SelectItem>
                <SelectItem value="video">Video</SelectItem>
                <SelectItem value="link">External Link</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <label className="text-sm font-medium mb-2 block">Upload File</label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
              <input
                type="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.mp4,.avi,.mov,.wmv,.webm"
                onChange={(e) => setMaterialFile(e.target.files?.[0] || null)}
                className="w-full"
              />
              {materialFile && (
                <div className="mt-2 flex items-center text-sm text-green-600">
                  <FileText className="h-4 w-4 mr-1" />
                  {materialFile.name}
                </div>
              )}
            </div>
          </div>

          <Button 
            onClick={handleSubmit} 
            disabled={uploading || !formData.title_en || !formData.title_el || !materialFile}
          >
            {uploading ? 'Uploading...' : 'Add Material'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};